/**
 * Block F.4: детальная страница admin для одного партнёра.
 * Показывает: метрики (боты, подписки, revenue, margin, balance), действия.
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { PartnerBotBrief } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSheet } from '@/components/MessageSheet';
import {
  Crown,
  Bot as BotIcon,
  Users,
  TrendingUp,
  Wallet,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import { getWebApp, haptic } from '@/lib/telegram';

export const Route = createFileRoute('/partners/$id')({ component: PartnerDetail });

function PartnerDetail() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const { data: p, isPending } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => api.partnerDetail(id),
  });

  const [msgOpen, setMsgOpen] = useState(false);
  const toggleApi = useMutation({
    mutationFn: (enabled: boolean) => api.setPartnerApiEnabled(id, enabled),
    onSuccess: (r) => {
      haptic('success');
      toast.success(r.api_enabled ? 'VPN API открыт' : 'VPN API закрыт');
      void qc.invalidateQueries({ queryKey: ['partner', id] });
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось изменить доступ');
    },
  });
  const sendMsg = useMutation({
    mutationFn: (text: string) => api.messagePartner(id, text),
    onSuccess: () => {
      haptic('success');
      toast.success('Сообщение отправлено');
      setMsgOpen(false);
    },
    onError: () => {
      haptic('error');
      toast.error('Не доставлено: партнёр не открывал бота или заблокировал.');
    },
  });

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.show();
    const onBack = () => navigate({ to: '/partners' });
    tg?.BackButton.onClick(onBack);
    return () => {
      tg?.BackButton.offClick(onBack);
      tg?.BackButton.hide();
    };
  }, [navigate]);

  if (isPending || !p) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          {p.is_owner ? <Crown className="w-7 h-7" /> : <Users className="w-7 h-7" />}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold tracking-tight truncate">{p.display_name}</h2>
          <p className="text-xs text-muted-foreground">
            {p.is_owner ? 'Owner' : 'Partner'} · tg_id={p.tg_user_id ?? '—'}
          </p>
        </div>
        {p.is_active ? (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        ) : (
          <XCircle className="w-5 h-5 text-red-500" />
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat icon={<BotIcon className="w-4 h-4" />} label="Боты" value={`${p.bots_count}/${p.bot_limit}`} />
        <Stat icon={<Users className="w-4 h-4" />} label="Active subs" value={String(p.active_subscriptions)} />
        <Stat
          icon={<TrendingUp className="w-4 h-4" />}
          label="Revenue (30d)"
          value={`$${(p.revenue_minor / 100).toFixed(2)}`}
        />
        <Stat
          icon={<TrendingUp className="w-4 h-4" />}
          label="Margin (30d)"
          value={`$${(p.margin_minor / 100).toFixed(2)}`}
        />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <Row label="Баланс">
            <Wallet className="inline w-3 h-3 text-primary mr-1" />
            <span className="font-mono">${(p.balance_usd_minor / 100).toFixed(2)}</span>
          </Row>
          <Row label="Onboarding">
            {p.onboarding_completed ? (
              <span className="text-green-500">Завершён</span>
            ) : (
              <span className="text-yellow-500">В процессе</span>
            )}
          </Row>
          <Row label="Создан" value={new Date(p.created_at).toLocaleDateString('ru')} />
          <Row label="VPN API">
            <button
              type="button"
              disabled={toggleApi.isPending}
              onClick={() => toggleApi.mutate(!p.api_enabled)}
              className={
                p.api_enabled
                  ? 'rounded-lg bg-green-500/15 px-2 py-0.5 text-[12px] font-semibold text-green-500'
                  : 'rounded-lg bg-red-500/15 px-2 py-0.5 text-[12px] font-semibold text-red-500'
              }
            >
              {p.api_enabled ? 'открыт' : 'закрыт'}
              {p.has_api_key ? ' · ключ выпущен' : ''}
            </button>
          </Row>
        </CardContent>
      </Card>

      <Button
        variant="secondary"
        className="w-full"
        disabled={!p.tg_user_id}
        onClick={() => setMsgOpen(true)}
      >
        <MessageSquare className="w-4 h-4" /> Написать партнёру
      </Button>

      <BotsSection partnerId={id} />

      <MessageSheet
        open={msgOpen}
        onClose={() => setMsgOpen(false)}
        title={`Сообщение ${p.display_name}`}
        onSend={(text) => sendMsg.mutate(text)}
        pending={sendMsg.isPending}
      />
    </div>
  );
}

function BotsSection({ partnerId }: { partnerId: string }) {
  const { data: bots, isPending } = useQuery({
    queryKey: ['partner-bots', partnerId],
    queryFn: () => api.partnerBots(partnerId),
  });

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2 flex items-center gap-1.5">
        <BotIcon className="w-3.5 h-3.5" />
        Боты {bots ? `(${bots.length})` : ''}
      </h3>
      {isPending ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : !bots || bots.length === 0 ? (
        <Card>
          <CardContent className="p-5 text-center text-sm text-muted-foreground">
            Нет ботов
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {bots.map((b) => (
            <BotRow key={b.id} bot={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function BotRow({ bot }: { bot: PartnerBotBrief }) {
  return (
    <Link to="/bots/$id" params={{ id: bot.id }} preload="intent">
      <Card className="overflow-hidden active:bg-white/5 transition-colors">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">@{bot.username}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {bot.title} · {bot.clients_count} клиентов · {bot.active_subscriptions} подписок
            </div>
          </div>
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
              bot.is_active
                ? 'bg-green-500/15 text-green-500'
                : 'bg-white/10 text-muted-foreground'
            }`}
          >
            {bot.status}
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </CardContent>
      </Card>
    </Link>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="bg-card border-white/8">
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="text-primary">{icon}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-lg font-bold truncate">{value}</div>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-sm">{children || value || '—'}</span>
    </div>
  );
}
